import { CalendarProvider } from "@modules/calendar/context/calendarContext"
import { ProgramDetailsContent } from "@modules/programs/components/programDetailsContent"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramOnOffProgression } from "@modules/programs/hooks/useProgramsOnOff"
import { ProgramProgressionStatus } from "@modules/programs/types/programOnOffTypes"
import { ProgramSubscriptionStatus, ProgramType } from "@modules/programs/types/programTypes"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { useCallback, useMemo, useState } from "react"

type ProgramDetailsPageContentProps = {
  programId: string
} & React.ComponentProps<"div">

export function ProgramDetailsPageContent({ programId, ...props }: ProgramDetailsPageContentProps) {
  const [calendarRange, setCalendarRange] = useState<
    { startDate: number | undefined; endDate: number | undefined } | { startDate: string; endDate: string }
  >()

  const { data: program, isLoading: loading } = useProgram(programId)
  const isProgramOnOff = program.type === ProgramType.ONOFF
  const { isAuthor, isAcceptedSharedCoach } = useProgramRights(programId)
  const queryClient = useQueryClient()
  const { data: progression } = useProgramOnOffProgression(
    program.id,
    !isProgramOnOff ||
      !(
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION
      ),
  )

  const isUndatedProgram = useMemo(
    () =>
      isProgramOnOff &&
      (isAuthor ||
        isAcceptedSharedCoach ||
        !progression ||
        (progression.status !== ProgramProgressionStatus.STARTED &&
          progression.status !== ProgramProgressionStatus.COMPLETED)),
    [isAuthor, isProgramOnOff, isAcceptedSharedCoach, progression],
  )

  const canUserSeeSessions = useMemo(
    () =>
      isAuthor ||
      isAcceptedSharedCoach ||
      program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.PENDING_CANCELLATION ||
      program.subscriptionStatus === ProgramSubscriptionStatus.TRIALING,
    [isAuthor, isAcceptedSharedCoach, program.subscriptionStatus],
  )

  const onDateSet = useCallback(
    ({
      startDate,
      endDate,
    }: { startDate: number; endDate: number } | { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs }) => {
      if (program && !loading) {
        if (isUndatedProgram) {
          if (typeof startDate === "number" && typeof endDate === "number") setCalendarRange({ startDate, endDate })
        } else {
          setCalendarRange({ startDate: startDate.toString(), endDate: endDate.toString() })
        }
      }
    },
    [program, loading, setCalendarRange, isUndatedProgram],
  )
  const refreshEvents = useCallback(
    ({
      startDate,
      endDate,
    }: { startDate: number; endDate: number } | { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs }) => {
      if (program) {
        if (typeof startDate === "number" && typeof endDate === "number") {
          setCalendarRange({ startDate, endDate })
        } else {
          setCalendarRange({ startDate: startDate.toString(), endDate: endDate.toString() })
        }
        queryClient.invalidateQueries({ queryKey: ["programSessions"] })
      }
    },
    [program, isUndatedProgram, queryClient],
  )

  const initialDate = useMemo(() => {
    return isUndatedProgram ? undefined : dayjs().startOf("day")
  }, [isUndatedProgram])

  const currentDayIndex = useMemo(() => {
    return isProgramOnOff && progression ? progression.currentIndex : undefined
  }, [isProgramOnOff, progression])

  return (
    <CalendarProvider
      isUndated={isUndatedProgram}
      initialDate={initialDate}
      maxDayIndex={(isProgramOnOff && program.duration) || undefined}
      currentDayIndex={currentDayIndex}
      onDateSet={onDateSet}
      refreshEvents={refreshEvents}
      defaultDisplayDetailedEvents={canUserSeeSessions}
      defaultView="dayGridWeek"
    >
      <ProgramDetailsContent programId={programId} calendarRange={calendarRange} {...props} />
    </CalendarProvider>
  )
}
